import { useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BrainCircuit, Check, Loader2, MessageSquarePlus, Send, Sparkles } from "lucide-react";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Section, SectionHeading } from "@/components/site/Section";
import { EmptyState, Loading, Notice, ProblemAlert } from "@/components/site/Feedback";
import { ManageOnWebsite } from "@/components/site/Membership";
import { api, ApiError } from "@/lib/api";
import { useCheckout } from "@/lib/hooks";
import { isMobile } from "@/lib/config";
import { money, relative } from "@/lib/format";
import { AnalyzerMessageStatus, type AnalyzerMessageDto, type AnalyzerPricingDto, type CreditPackageDto } from "@/lib/types";
import { cn } from "@/lib/utils";

const isSimulatedAnswer = (m: AnalyzerMessageDto, simulatedIds: Set<string>) =>
  simulatedIds.has(m.id) || (m.answer ?? "").startsWith("(Simulated answer");

const scrollToPackages = () => document.getElementById("analyzer-packages")?.scrollIntoView({ behavior: "smooth" });

export function DealAnalyzerPage() {
  usePageTitle("AI Deal Analyzer");
  const queryClient = useQueryClient();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [question, setQuestion] = useState("");
  const [simulatedIds, setSimulatedIds] = useState<Set<string>>(new Set());
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);

  const credits = useQuery({ queryKey: ["billing", "credits"], queryFn: api.billing.credits });
  const conversations = useQuery({ queryKey: ["analyzer", "conversations"], queryFn: api.analyzer.conversations });
  const messages = useQuery({
    queryKey: ["analyzer", "messages", conversationId],
    queryFn: () => api.analyzer.messages(conversationId!),
    enabled: conversationId !== null,
  });

  const ask = useMutation({
    mutationFn: (q: string) => api.analyzer.ask(q, conversationId),
    onMutate: (q) => setPendingQuestion(q),
    onSuccess: (r) => {
      setQuestion("");
      if (r.simulated) setSimulatedIds((s) => new Set(s).add(r.message.id));
      queryClient.setQueryData(["billing", "credits"], (c: { mentorship: number; analyzer: number } | undefined) =>
        c ? { ...c, analyzer: r.remainingCredits } : c,
      );
      setConversationId(r.message.conversationId);
      void queryClient.invalidateQueries({ queryKey: ["analyzer"] });
    },
    onSettled: () => setPendingQuestion(null),
  });

  const list = [...(messages.data?.items ?? [])].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const lastSimulated = ask.data?.simulated;
  const noCredits = ask.error instanceof ApiError && ask.error.isInsufficientCredits;
  const balance = credits.data?.analyzer;

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [list.length, pendingQuestion]);

  const submit = () => {
    const q = question.trim();
    if (!q || ask.isPending) return;
    ask.mutate(q);
  };

  return (
    <>
      <section className="surface-ink">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-5 py-14 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="eyebrow text-gold">The flagship tool</p>
            <h1 className="mt-3 text-4xl text-ink-foreground md:text-5xl">The AI Deal Analyzer</h1>
            <div className="hairline-gold mt-5" />
            <p className="mt-5 max-w-2xl text-ink-foreground/70">
              Ask George about any UK property or listing: valuation evidence, rental estimates, yields and the
              points to check before you offer. Every question uses one analyzer credit.
            </p>
          </div>
          <div className="rounded-lg border border-ink-foreground/15 bg-ink-foreground/5 px-6 py-4 text-ink-foreground">
            <p className="eyebrow text-gold">Credits left</p>
            <p className="font-display text-4xl">{credits.isPending ? "…" : (balance ?? 0)}</p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-5 py-10">
        <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
          <aside className="h-fit rounded-lg border border-border bg-card shadow-card">
            <div className="border-b border-border p-4">
              <Button
                variant="outlineGold"
                size="sm"
                className="w-full"
                onClick={() => {
                  setConversationId(null);
                  ask.reset();
                }}
              >
                <MessageSquarePlus />
                New conversation
              </Button>
            </div>
            <div className="max-h-[28rem] overflow-y-auto p-2">
              {conversations.isPending ? (
                <Loading className="px-2 py-4" />
              ) : conversations.isError ? (
                <ProblemAlert error={conversations.error} className="m-2" />
              ) : conversations.data.length === 0 ? (
                <p className="px-3 py-4 text-sm text-muted-foreground">Your conversations appear here.</p>
              ) : (
                conversations.data.map((c) => (
                  <button
                    key={c.conversationId}
                    onClick={() => {
                      setConversationId(c.conversationId);
                      ask.reset();
                    }}
                    className={cn(
                      "block w-full rounded-md px-3 py-2.5 text-left transition-colors hover:bg-accent/70",
                      conversationId === c.conversationId && "bg-accent",
                    )}
                  >
                    <span className="line-clamp-2 text-sm">{c.title}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {c.messageCount} message{c.messageCount === 1 ? "" : "s"} · {relative(c.lastMessageAt)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </aside>

          <div className="flex min-h-[32rem] flex-col rounded-lg border border-border bg-card shadow-card">
            <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <BrainCircuit className="size-4 text-gold" />
                George · PropertyData
              </div>
              {lastSimulated && (
                <Badge variant="outline" className="border-gold/60 bg-gold/10">
                  <Sparkles className="mr-1 size-3" /> Simulated answer (POC)
                </Badge>
              )}
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto px-5 py-6">
              {conversationId === null && !pendingQuestion && (
                <EmptyState title="Ask your first question">
                  e.g. "Is a 3-bed terrace in Bolton BL3 at £135,000 a good buy-to-let?"
                </EmptyState>
              )}
              {conversationId !== null && messages.isPending && <Loading />}
              {messages.isError && <ProblemAlert error={messages.error} />}
              {list.map((m) => (
                <MessageView key={m.id} message={m} simulated={isSimulatedAnswer(m, simulatedIds)} />
              ))}
              {pendingQuestion && (
                <div className="space-y-3">
                  <QuestionBubble text={pendingQuestion} />
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin text-gold" /> George is analysing…
                  </div>
                </div>
              )}
              <div ref={bottom} />
            </div>

            <div className="border-t border-border p-4">
              {noCredits ? (
                <Notice tone="warning" title="You're out of analyzer credits" className="mb-3">
                  {isMobile ? (
                    "Credits can be purchased on our website."
                  ) : (
                    <>
                      Buy a credit package to keep asking.{" "}
                      <button className="font-medium text-foreground underline underline-offset-4" onClick={scrollToPackages}>
                        See packages
                      </button>
                    </>
                  )}
                </Notice>
              ) : (
                ask.isError && <ProblemAlert error={ask.error} className="mb-3" />
              )}
              <form
                className="flex items-end gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit();
                }}
              >
                <Textarea
                  rows={2}
                  maxLength={2000}
                  value={question}
                  placeholder="Ask about a property, a postcode or paste a listing link…"
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  className="min-h-[3rem] resize-none"
                />
                <Button type="submit" variant="gold" size="lg" className="px-5" disabled={!question.trim() || ask.isPending}>
                  {ask.isPending ? <Loader2 className="animate-spin" /> : <Send />}
                  <span className="sr-only sm:not-sr-only">Ask</span>
                </Button>
              </form>
              <p className="mt-2 text-xs text-muted-foreground">
                Answers are estimates for education, not financial advice. One question = one credit.
              </p>
            </div>
          </div>
        </div>
      </div>

      <AnalyzerPackages />
    </>
  );
}

function QuestionBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] whitespace-pre-wrap rounded-lg rounded-br-sm bg-primary px-4 py-3 text-sm text-primary-foreground">
        {text}
      </div>
    </div>
  );
}

/** George answers in Markdown (bold figures, lists, sometimes tables). */
function AnswerMarkdown({ text }: { text: string }) {
  return (
    <div className="space-y-2 leading-relaxed">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="whitespace-pre-wrap">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
          ul: ({ children }) => <ul className="list-disc space-y-1 ps-5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal space-y-1 ps-5">{children}</ol>,
          h1: ({ children }) => <p className="font-display text-lg">{children}</p>,
          h2: ({ children }) => <p className="font-display text-lg">{children}</p>,
          h3: ({ children }) => <p className="font-semibold">{children}</p>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noreferrer" className="underline underline-offset-4">
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-xs">{children}</table>
            </div>
          ),
          th: ({ children }) => <th className="border-b border-border px-2 py-1 font-semibold">{children}</th>,
          td: ({ children }) => <td className="border-b border-border/60 px-2 py-1">{children}</td>,
        }}
      >
        {text}
      </Markdown>
    </div>
  );
}

function MessageView({ message, simulated }: { message: AnalyzerMessageDto; simulated: boolean }) {
  return (
    <div className="space-y-3">
      <QuestionBubble text={message.question} />
      <div className="max-w-[92%] rounded-lg rounded-bl-sm border border-border bg-background px-4 py-3 text-sm">
        {message.status === AnalyzerMessageStatus.Failed ? (
          <p className="text-destructive">{message.error ?? "The question could not be answered. Your credit was refunded."}</p>
        ) : message.status === AnalyzerMessageStatus.Pending ? (
          <p className="text-muted-foreground">Waiting for the answer…</p>
        ) : (
          <AnswerMarkdown text={message.answer ?? ""} />
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{relative(message.createdAt)}</span>
          {message.creditsUsed > 0 && <span>· {message.creditsUsed} credit{message.creditsUsed === 1 ? "" : "s"}</span>}
          {simulated && (
            <Badge variant="outline" className="border-gold/60 bg-gold/10 text-[0.65rem]">
              Simulated answer (POC)
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

function AnalyzerPackages() {
  const packages = useQuery({ queryKey: ["analyzer", "packages"], queryFn: api.analyzer.packages });
  const pricing = useQuery({ queryKey: ["analyzer", "pricing"], queryFn: api.analyzer.pricing });
  return (
    <Section className="pt-6" >
      <div id="analyzer-packages" className="scroll-mt-24">
        <SectionHeading
          eyebrow="Analyzer credits"
          title="Top up whenever you need"
          subtitle={
            pricing.data
              ? `One credit is one question to George and costs ${money(pricing.data.creditPrice, pricing.data.currency)}. Credits are added to your balance straight after payment.`
              : "Credits are added to your balance straight after payment."
          }
        />
        <div className="mt-10">
          {packages.isPending ? (
            <Loading />
          ) : packages.isError ? (
            <ProblemAlert error={packages.error} />
          ) : (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {pricing.data && !isMobile && <CustomCreditsCard pricing={pricing.data} />}
              {[...packages.data]
                .sort((a, b) => a.order - b.order)
                .map((p) => (
                  <CreditPackageCard key={p.id} pkg={p} />
                ))}
            </div>
          )}
          {isMobile && <ManageOnWebsite className="mt-6">Credits are purchased on our website.</ManageOnWebsite>}
        </div>
      </div>
    </Section>
  );
}

/** Any number of credits at the credit price. */
function CustomCreditsCard({ pricing }: { pricing: AnalyzerPricingDto }) {
  const checkout = useCheckout();
  const [credits, setCredits] = useState(String(Math.max(pricing.minCredits, 10)));
  const value = Number.parseInt(credits, 10);
  const valid = Number.isInteger(value) && value >= pricing.minCredits && value <= pricing.maxCredits;
  return (
    <div className="flex flex-col rounded-lg border border-gold bg-card p-7 shadow-card">
      <p className="eyebrow text-gold">Choose your amount</p>
      <p className="mt-3 font-display text-4xl">{valid ? money(value * pricing.creditPrice, pricing.currency) : "—"}</p>
      <p className="mt-1 text-xs text-muted-foreground">{money(pricing.creditPrice, pricing.currency)} per question</p>
      <div className="mt-5 flex-1 space-y-2">
        <Label htmlFor="custom-credits">Credits</Label>
        <Input
          id="custom-credits"
          type="number"
          inputMode="numeric"
          min={pricing.minCredits}
          max={pricing.maxCredits}
          value={credits}
          onChange={(e) => setCredits(e.target.value)}
        />
        {!valid && (
          <p className="text-xs text-destructive">
            Between {pricing.minCredits} and {pricing.maxCredits} credits.
          </p>
        )}
      </div>
      <Button
        variant="gold"
        className="mt-6 w-full"
        disabled={!valid || checkout.isPending}
        onClick={() => checkout.mutate({ kind: "analyzer-credits", credits: value })}
      >
        {checkout.isPending && <Loader2 className="animate-spin" />}
        Buy {valid ? value : ""} credit{value === 1 ? "" : "s"}
      </Button>
    </div>
  );
}

function CreditPackageCard({ pkg }: { pkg: CreditPackageDto }) {
  const checkout = useCheckout();
  return (
    <div
      className={cn(
        "flex flex-col rounded-lg bg-card p-7",
        pkg.isFeatured ? "border-2 border-gold shadow-elevated" : "border border-border shadow-card",
      )}
    >
      <p className="eyebrow text-gold">{pkg.name}</p>
      <p className="mt-3 font-display text-4xl">{money(pkg.price, pkg.currency)}</p>
      {pkg.subtitle && <p className="mt-1 text-xs text-muted-foreground">{pkg.subtitle}</p>}
      <ul className="mt-5 flex-1 space-y-2">
        {pkg.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check className="mt-0.5 size-4 shrink-0 text-gold" />
            {f}
          </li>
        ))}
      </ul>
      {!isMobile && (
        <Button
          variant={pkg.isFeatured ? "gold" : "outlineGold"}
          className="mt-6 w-full"
          disabled={checkout.isPending}
          onClick={() => checkout.mutate({ kind: "package", id: pkg.id })}
        >
          {checkout.isPending && <Loader2 className="animate-spin" />}
          Buy {pkg.credits} credits
        </Button>
      )}
    </div>
  );
}
